import {AttrsSchema, Component} from 'panel';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import zip from 'lodash/zip';

import {SelectionChangeEvent, SortChangeEvent} from 'common/components/table/types';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/models/funnels/constants';
import {FunnelSegment, FunnelSelectionChangeEventDetail, SelectMode} from 'common/report/models/funnels/types';
import {computeSelectionsOnSelectionChange, computeSelectionsOnSortChange} from 'common/report/util/funnels/utils';
import {DataTableAttrs, Theme} from 'common/components/data-table/types';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {reconcileComparisonSegments} from 'common/report/data/funnels/step-formatter';
import {
  DEFAULT_SELECTED_SEGMENTS_LIMIT,
  MAX_SELECTED_SEGMENTS_LIMIT,
  PropertyType,
  ReportName,
} from 'common/report/constants';

import {template} from './template';
import css from './index.styl?inline';
import {FunnelTableRow} from './types';
import {DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, getColumnGroups, getRows} from './utils';

const OVERALL_PROPS = [{prop: OVERALL_PROP, propName: OVERALL_PROP_NAME}];

const ROWS_CACHE_INVALIDATION_ATTRS = [`control-group`, `should-show-stat-sig`, `segment-rename-steps`];

export type FunnelsTableAttrs = {
  'is-waiting-for-schedule-analysis': boolean;
  'segment-names': string[];
  'control-group': string[];
  'segment-type': string;
  'step-names': string[];
  'default-selections': number;
  selected: ColorChoice[];
  'selection-limit': number;
  'segment-rename-steps': string[];
  'start-step': number;
  'should-show-stat-sig': boolean;
  mode: SelectMode;
  'has-overall-row': boolean;
  'download-mode': boolean;
  'sort-field': string;
  'sort-order': number;
  'selected-columns': TODO;
  'show-oda-footer': boolean;
  'show-on-demand-analysis-upsell': boolean;
  'table-container-width': number;
  'segmentation-data-table': boolean;
};

export class FunnelsTable extends Component<EmptyObj, FunnelsTableAttrs> {
  private _segmentSteps: FunnelSegment[] = [];
  private _comparisonSegments?: FunnelSegment[];
  private _rowsCache?: {rows: FunnelTableRow[]; selected: Map<string, string>};
  private _lastSelectionActionWasUnselect = false;

  static get attrsSchema(): AttrsSchema<FunnelsTableAttrs> {
    return {
      'is-waiting-for-schedule-analysis': `boolean`,
      'segment-names': {type: `json`, default: []},
      'control-group': {type: `json`, default: []},
      'segment-type': `string`,
      'step-names': `json`,
      'default-selections': {
        type: `number`,
        description: `how many rows to select by default`,
        default: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
      },
      selected: `json`,
      'selection-limit': {
        type: `number`,
        description: `maximum number of item selections to allow`,
        default: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Funnels],
      },
      'segment-rename-steps': {
        type: `json`,
        default: null,
        description: `allows rename map changes to trigger re-render even if data hasn't changed`,
      },
      'start-step': {type: `number`, default: 0},
      'should-show-stat-sig': `boolean`,
      'show-oda-footer': `boolean`,
      'show-on-demand-analysis-upsell': `boolean`,
      mode: {
        type: `string`,
        enum: Object.values(SelectMode),
        default: SelectMode.Position,
      },
      'has-overall-row': `boolean`,
      'download-mode': `boolean`,
      'sort-order': `number`,
      'sort-field': `string`,
      'selected-columns': {
        type: `json`,
        default: [],
      },
      'table-container-width': `number`,
      'segmentation-data-table': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      helpers: {
        defaultControlGroup: OVERALL_PROP,
        clickAnalyze: () => this.dispatchEvent(new CustomEvent(`clickAnalyze`)),

        getNumOfStickyRows: () => {
          let numRows = 0;
          if (this.attr(`has-overall-row`)) {
            // There are two overall rows in a comparison
            numRows += this.helpers.hasComparison() ? 2 : 1;
          }

          // Sticky rows for overall row and control group row (if control group is not overall)
          const controlGroup = this.attr(`control-group`);
          if (controlGroup && controlGroup.length && !isEqual(controlGroup, [OVERALL_PROP])) {
            numRows += this.helpers.hasComparison() ? 2 : 1;
          }
          return numRows;
        },
        getDefaultSortOrder: () => (this.attr(`sort-order`) ? this.attr(`sort-order`) : DEFAULT_SORT_ORDER),
        getDefaultSortField: () => (this.attr(`sort-field`) ? this.attr(`sort-field`) : DEFAULT_SORT_FIELD),
        getMaxBodyHeight: () => (this.attr(`download-mode`) ? `100%` : `600px`),
        getDataTableAttrs: (): DataTableAttrs => {
          return {
            'default-sort-field': this.helpers.getDefaultSortField(),
            'default-sort-order': this.helpers.getDefaultSortOrder(),
            'max-body-height': this.helpers.getMaxBodyHeight(),
            'num-sticky-rows': this.helpers.getNumOfStickyRows(),
            'multi-select': this.helpers.isSelectable(),
            'selection-limit': this.attr(`selection-limit`),
            'track-name': `funnels-table`,
            'has-comparison': this.helpers.hasComparison(),
            theme: Theme.Funnels,
            'segmentation-data-table': this.attr(`segmentation-data-table`),
          };
        },
        getColumns: () =>
          getColumnGroups({
            helpers: this.helpers,
            stepNames: this.attr(`step-names`),
            segmentNames: this.attr(`segment-names`),
            segmentType: this.attr(`segment-type`) as PropertyType,
            selectedColumns: this.attr(`selected-columns`),
            tableWidth: this.attr(`table-container-width`),
            shouldShowStatSig: this.attr(`should-show-stat-sig`),
          }),

        getRows: () => {
          // ts-related: map<[string, string]> is needed to cast the result to a tuple for the Map constructor
          const selected = new Map(
            this.selected.map<[string, string]>((s) => [s.key, s.color]),
          );

          const cachedRows = this.getCachedRows(selected);
          if (cachedRows) {
            // Cache is invalidated in attributeChangedCallback for any ROWS_CACHE_INVALIDATION_ATTRS,
            // and whenever segmentSteps or comparisonSegments change (in those setter methods)
            return cachedRows;
          }

          const controlGroup = this.attr(`control-group`);
          const shouldShowStatSig = this.attr(`should-show-stat-sig`);
          let rows = getRows({
            selected,
            controlGroup,
            shouldShowStatSig,
            segmentSteps: this.segmentSteps,
            shouldFilterForSelected: this.attr(`download-mode`),
          });
          if (this.helpers.hasComparison()) {
            const comparisonRows = getRows({
              selected,
              controlGroup,
              shouldShowStatSig,
              segmentSteps: reconcileComparisonSegments({
                segments: this.segmentSteps,
                comparisonSegments: this.comparisonSegments,
                updatePropName: true,
              }),
              shouldFilterForSelected: this.attr(`download-mode`),
            });
            rows = flatten(zip(rows, comparisonRows));
          }

          this.cacheRows({rows, selected});
          return rows;
        },

        hasComparison: () => Boolean(this._comparisonSegments),

        isSelectable: () => !isEmpty(this.attr(`segment-names`)),
        onSelectionChange: (ev: SelectionChangeEvent<FunnelTableRow>) => {
          const {isToggleAll} = ev.detail;
          this._lastSelectionActionWasUnselect = isToggleAll && this.selected.length > 0;

          let orderedRowIds = ev.detail.rows.map((row) => row.prop);
          if (this.helpers.hasComparison()) {
            // Time comparison rows have the same id as the original rows, so need to dedupe them.
            orderedRowIds = [...new Set(orderedRowIds)];
          }
          const {selected, selectMode} = computeSelectionsOnSelectionChange({
            isToggleAll,
            prevSelected: this.selected.map((s) => s.key),
            orderedRowIds,
            maxNumSelections: this.attr(`selection-limit`),
            changed: isToggleAll
              ? null
              : Object.fromEntries(ev.detail.changed.map((change) => [change.row.prop, change.isSelected])),
          });
          this._emitSelection({selected, selectMode});
        },

        onSortChange: (ev: SortChangeEvent<FunnelTableRow>) => {
          // when we unselect all, don't listen to the next sort change
          if (this._lastSelectionActionWasUnselect) {
            return;
          }

          let orderedRowIds = ev.detail.rows.map((row) => row.prop);
          if (this.helpers.hasComparison()) {
            // Time comparison rows have the same id as the original rows, so need to dedupe them.
            orderedRowIds = [...new Set(orderedRowIds)];
          }

          const {selected, selectMode} = computeSelectionsOnSortChange({
            prevSelected: this.selected.map((s) => s.key),
            prevSelectMode: this.attr(`mode`),
            orderedRowIds,
            maxNumSelections: this.attr(`selection-limit`),
            defaultNumSelections: this.attr(`default-selections`),
          });
          this._emitSelection({
            selected,
            selectMode,
            sortOrder: ev.detail.sortOrder,
            sortField: ev.detail.sortField,
          });
        },

        setControl: (_, newControl) => {
          let props = newControl.props;
          if (props[0].prop === OVERALL_PROP) {
            props = OVERALL_PROPS;
          }
          this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props}}));
        },

        removeControl: () => {
          this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props: OVERALL_PROPS}}));
        },
      },
    };
  }

  public attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    if (ROWS_CACHE_INVALIDATION_ATTRS.includes(name)) {
      this.clearRowsCache();
    }
  }

  private _emitSelection(selectionChangeParams: FunnelSelectionChangeEventDetail) {
    this.dispatchEvent(new CustomEvent(`selectionChange`, {detail: selectionChangeParams}));
  }

  public get selected(): Array<{color: string; key: string}> {
    return this.attr(`selected`);
  }

  private _renameFunnelSegments(data: FunnelSegment[]): FunnelSegment[] {
    const renameMap = this.attr(`segment-rename-steps`);
    return data.map((item) => {
      if (item.prop in renameMap) {
        item.propName = renameMap[item.prop];
      }
      return item;
    });
  }

  public set segmentSteps(val: FunnelSegment[]) {
    this.clearRowsCache();
    this._segmentSteps = val;
  }
  public get segmentSteps(): FunnelSegment[] {
    return this._renameFunnelSegments(this._segmentSteps);
  }

  public set comparisonSegments(val: FunnelSegment[]) {
    this.clearRowsCache();
    this._comparisonSegments = val;
  }
  public get comparisonSegments(): FunnelSegment[] {
    return this._renameFunnelSegments(this._comparisonSegments);
  }

  /**
   * Get cached rows. Updates them for new 'selected' as needed.
   */
  private getCachedRows(selected: Map<string, string>): FunnelTableRow[] | false {
    if (!this._rowsCache) {
      return false;
    }

    if (this.selectedChanged(this._rowsCache.selected, selected)) {
      this._rowsCache.rows.forEach((row) => {
        row.isSelected = selected.has(row.prop);
        row.checkedColor = selected.get(row.prop);
      });
      this._rowsCache.selected = selected;
    }
    return this._rowsCache.rows;
  }

  private cacheRows({rows, selected}: {rows: FunnelTableRow[]; selected: Map<string, string>}) {
    this._rowsCache = {rows, selected};
  }

  private clearRowsCache() {
    this._rowsCache = undefined;
  }

  private selectedChanged(oldSelected: Map<string, string>, newSelected: Map<string, string>) {
    if (oldSelected.size !== newSelected.size) {
      return true;
    }
    for (const [key, val] of oldSelected) {
      if (newSelected.get(key) !== val) {
        return true;
      }
    }
    return false;
  }
}
export const FunnelsTableEl = defineAndMakeJsxFactory(`mp-funnels-table`, FunnelsTable);
